import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MailCheck, MailWarning } from "lucide-react";
import { toast } from "sonner";

const DISMISS_KEY = "pp_verify_banner_hidden_at";
const HIDE_HOURS = 12; // reappear after 12h if the user snoozed

/**
 * Slim orange banner shown at the top of every authenticated page when the
 * user's email is not yet verified. Calls POST /api/auth/resend-verification.
 * Admins are exempt (they're seeded and pre-trusted).
 */
export function EmailVerifiedBanner() {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const at = localStorage.getItem(DISMISS_KEY);
    if (at) {
      const ageMs = Date.now() - Number(at);
      if (ageMs < HIDE_HOURS * 3600 * 1000) setDismissed(true);
    }
  }, []);

  // Only show for authenticated non-admin users who explicitly are NOT verified.
  // `email_verified === undefined` on legacy users → treat as verified so we don't
  // flood every seeded account with a banner they can't act on.
  if (!user || user.role === "admin") return null;
  if (user.email_verified !== false) return null;
  if (dismissed) return null;

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification", { email: user.email });
      toast.success("Check your inbox for a fresh verification link.");
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Couldn't send the link");
    } finally {
      setSending(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <div
      className="w-full flex items-center justify-center gap-3 px-4 py-2 text-sm bg-amber-500 text-white shadow-sm"
      data-testid="verify-email-banner"
      role="status"
    >
      <MailWarning className="h-4 w-4 shrink-0" />
      <span className="truncate">
        <b>Verify your email.</b>
        <span className="opacity-90 hidden md:inline"> We sent a link to {user.email} — click it to unlock statements, ARC updates, and alerts.</span>
      </span>
      <Button
        size="sm"
        variant="outline"
        onClick={resend}
        disabled={sending}
        className="bg-white/15 border-white/30 text-white hover:bg-white/25 hover:text-white shrink-0"
        data-testid="verify-email-resend"
      >
        <MailCheck className="h-3.5 w-3.5 mr-1" />
        {sending ? "Sending…" : "Resend link"}
      </Button>
      <button
        onClick={dismiss}
        className="text-white/80 hover:text-white text-xs px-2"
        data-testid="verify-email-dismiss"
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}
